<template>
    <nav class="tp" :class="['tp--' + tone, { 'tp--bordered': bordered }]" aria-label="Paginación de la tabla">
        <span v-if="showRange" class="tp__range" aria-live="polite">
            {{ range.from }}–{{ range.to }} de <strong>{{ totalElements.toLocaleString('es-MX') }}</strong><template v-if="itemLabel"> {{ itemLabel }}</template>
        </span>

        <label v-if="showRows" class="tp__rows">
            <span>Filas</span>
            <select class="tp__select" :value="itemsPerPage" aria-label="Filas por página" @change="onRows">
                <option v-for="size in sizes" :key="size" :value="size">{{ size }}</option>
            </select>
        </label>

        <label v-if="canJump" class="tp__jump">
            <span>Ir a</span>
            <input class="tp__select tp__input" type="number" inputmode="numeric" min="1" :max="pageCount"
                :placeholder="'1–' + pageCount" aria-label="Ir a la página" @change="onJump">
        </label>

        <div class="tp__pager">
            <button type="button" class="tp__btn" aria-label="Página anterior"
                :disabled="page <= 1" @click="go(page - 1)">
                <i class="fas fa-chevron-left" aria-hidden="true"></i>
            </button>
            <template v-for="(item, index) in pages">
                <span v-if="item === ellipsis" :key="'gap-' + index" class="tp__gap" aria-hidden="true">{{ ellipsis }}</span>
                <button v-else :key="item" type="button" class="tp__btn tp__page"
                    :class="{ 'is-current': item === page }" :aria-current="item === page ? 'page' : null"
                    :aria-label="'Página ' + item" @click="go(item)">{{ item }}</button>
            </template>
            <span class="tp__compact" aria-hidden="true">{{ page }} / {{ pageCount }}</span>
            <button type="button" class="tp__btn" aria-label="Página siguiente"
                :disabled="page >= pageCount" @click="go(page + 1)">
                <i class="fas fa-chevron-right" aria-hidden="true"></i>
            </button>
        </div>
    </nav>
</template>
<script>
import { ELLIPSIS, clampPage, pageRange, pageWindow } from '@/utils/pageWindow';

/**
 * Pie de las tablas con paginación propia (las que no usan el pie de Vuetify).
 * Se ve igual que `.v-data-footer` (main.css): rango a la izquierda, filas por
 * página y páginas a la derecha. En celular se reduce a "‹ 1 / 4 ›".
 *
 * Mismas props de siempre, así que las vistas no cambian. Elegir una página o
 * un tamaño llama a `initialize({ page, itemsPerPage })`; el tamaño es una
 * lista (25, 50, 100) en vez de un campo libre.
 *
 * Opcionales (el default conserva lo anterior):
 * - `itemLabel`: "1–25 de 736 registros"; '' lo deja en "1–25 de 736".
 * - `jumpFrom`: con más páginas que esto aparece "Ir a"; 0 lo desactiva.
 * - `tone`: color de la página actual y del foco (token `--app-<tone>`).
 * - `bordered`: borde completo con esquinas redondas en vez de la línea de arriba.
 * - `showRange` / `showRows`: ocultan el rango o el selector de filas.
 */
const PAGE_SIZES = [25, 50, 100];
const TONES = ['primary', 'success', 'info', 'warning', 'danger', 'neutral'];

export default {
    name: 'PaginationNumber',
    props: {
        itemsPerPage: { type: Number, default: 25 },
        pageCount: { type: Number, default: 10 },
        totalElements: { type: Number, default: 0 },
        page: { type: Number, default: 1 },
        initialize: { type: Function },
        itemLabel: { type: String, default: 'registros' },
        jumpFrom: { type: Number, default: 20 },
        tone: { type: String, default: 'primary', validator: (value) => TONES.includes(value) },
        bordered: { type: Boolean, default: false },
        showRange: { type: Boolean, default: true },
        showRows: { type: Boolean, default: true }
    },
    computed: {
        canJump() {
            return this.jumpFrom > 0 && this.pageCount > this.jumpFrom;
        },
        ellipsis() {
            return ELLIPSIS;
        },
        // si la vista arrancó con otro tamaño (10, 15…), se conserva en la lista
        sizes() {
            return PAGE_SIZES.includes(this.itemsPerPage)
                ? PAGE_SIZES
                : [...PAGE_SIZES, this.itemsPerPage].sort((a, b) => a - b);
        },
        pages() {
            return pageWindow(this.page, this.pageCount);
        },
        range() {
            return pageRange(this.page, this.itemsPerPage, this.totalElements);
        }
    },
    methods: {
        load({ page = 1, itemsPerPage = 25 }) {
            if (this.initialize) this.initialize({ page, itemsPerPage });
        },
        go(page) {
            if (page < 1 || page > this.pageCount || page === this.page) return;
            this.load({ page, itemsPerPage: this.itemsPerPage });
        },
        // "Ir a": se acota a 1..pageCount y el campo se vacía; un valor inválido no consulta
        onJump(event) {
            const target = clampPage(event.target.value, this.pageCount);
            event.target.value = '';
            if (target !== null) this.go(target);
        },
        // cambiar el tamaño vuelve a la primera página: la actual puede ya no existir
        onRows(event) {
            const itemsPerPage = parseInt(event.target.value, 10);
            if (!itemsPerPage || itemsPerPage < 1) return;
            this.load({ page: 1, itemsPerPage });
        }
    }
};
</script>
<style scoped>
/* tono: la página actual y el foco usan --tp-accent; primary conserva --app-primary-soft */
.tp {
    --tp-accent: var(--app-primary);
    --tp-soft: var(--app-primary-soft);
}
.tp--success { --tp-accent: var(--app-success); }
.tp--info { --tp-accent: var(--app-info); }
.tp--warning { --tp-accent: var(--app-warning); }
.tp--danger { --tp-accent: var(--app-danger); }
.tp--neutral { --tp-accent: var(--app-text); }
.tp--success,
.tp--info,
.tp--warning,
.tp--danger,
.tp--neutral {
    --tp-soft: color-mix(in srgb, var(--tp-accent) 14%, transparent);
}
.tp {
    /* las vistas lo ponen dentro de `v-card-actions` (flex): sin esto se queda del ancho de su contenido */
    flex: 1 1 100%;
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 16px;
    min-height: 48px;
    padding: 4px 12px;
    border-top: 1px solid var(--app-line);
    font-size: 12px;
    color: var(--app-text-muted);
}
.tp--bordered {
    border: 1px solid color-mix(in srgb, var(--tp-accent) 45%, var(--app-line));
    border-radius: 8px;
}
.tp__range {
    margin-right: auto;
}
.tp__jump {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}
.tp__input {
    width: 72px;
}
.tp__range strong {
    font-weight: 600;
    color: var(--app-text);
}
.tp__rows {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}
.tp__select {
    height: 32px;
    padding: 0 8px;
    border: 1px solid var(--app-line-strong);
    border-radius: 6px;
    background: var(--app-surface);
    color: var(--app-text);
    font: inherit;
    cursor: pointer;
}
.tp__pager {
    display: inline-flex;
    align-items: center;
    gap: 2px;
}
.tp__btn {
    min-width: 32px;
    height: 32px;
    padding: 0 6px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: var(--app-text);
    font: inherit;
    cursor: pointer;
}
.tp__btn:hover:not(:disabled) {
    background: var(--app-hover);
}
.tp__btn:disabled {
    color: var(--app-text-muted);
    opacity: .5;
    cursor: default;
}
.tp__btn:focus-visible,
.tp__select:focus-visible {
    outline: 2px solid var(--tp-accent);
    outline-offset: 1px;
}
.tp__page.is-current {
    background: var(--tp-soft);
    color: var(--tp-accent);
    font-weight: 600;
}
.tp__gap {
    min-width: 20px;
    text-align: center;
}
.tp__compact {
    display: none;
    padding: 0 8px;
    font-weight: 600;
    color: var(--app-text);
}
/* celular: sin números sueltos, solo "‹ 1 / 4 ›"; botones de 40px para tocarlos */
@media (max-width: 599px) {
    .tp__page,
    .tp__gap {
        display: none;
    }
    .tp__compact {
        display: inline;
    }
    .tp__btn {
        min-width: 40px;
        height: 40px;
    }
    .tp__pager {
        margin-left: auto;
    }
}
</style>
